import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { CHART_COLOURS } from '@/components/charts/ChartColours'
import { formatExchangeLogDayLabel } from '@/lib/exchangeHealth'

function LogTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const item = payload[0]?.payload
  if (!item) return null
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-md">
      <p className="font-medium text-foreground">{formatExchangeLogDayLabel(item.date)}</p>
      <p className="text-muted-foreground">
        {Number(item.count || 0).toLocaleString('en-GB')} log file
        {Number(item.count || 0) === 1 ? '' : 's'}
        {item.size_gb != null && Number(item.size_gb) > 0
          ? ` · ${Number(item.size_gb).toLocaleString('en-GB')} GB`
          : ''}
      </p>
    </div>
  )
}

export function ExchangeTransactionLogChart({ series }) {
  const rows = series ?? []
  if (rows.length === 0) return null

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Log files still on disk by last-write day (last 14 days). Growing bars usually mean
        truncation is not clearing older logs.
      </p>
      {rows.map((row) => {
        const chartData = (row.logs_by_day ?? []).map((day) => ({
          date: day.date,
          count: Number(day.count || 0),
          size_gb: Number(day.size_gb || 0),
        }))
        const total = Number(row.log_count || 0)
        const older = Number(row.older_than_window_count || 0)
        return (
          <div key={`${row.database}-${row.log_folder}`} className="space-y-2">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <p className="text-sm font-medium">{row.database || 'Mailbox database'}</p>
                {row.log_folder && (
                  <p className="text-xs text-muted-foreground break-all" title={row.log_folder}>
                    {row.log_folder}
                  </p>
                )}
              </div>
              <p className="text-xs text-muted-foreground tabular-nums">
                {total.toLocaleString('en-GB')} file{total === 1 ? '' : 's'}
                {row.total_size_gb != null ? ` · ${row.total_size_gb} GB` : ''}
                {older > 0 ? ` · ${older.toLocaleString('en-GB')} older than 14 days` : ''}
              </p>
            </div>
            <div className="h-36 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatExchangeLogDayLabel}
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    tickLine={false}
                    interval="preserveStartEnd"
                    minTickGap={18}
                  />
                  <YAxis
                    allowDecimals={false}
                    width={36}
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                    tickLine={false}
                  />
                  <Tooltip content={<LogTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={CHART_COLOURS[0]}
                    fill={CHART_COLOURS[0]}
                    fillOpacity={0.2}
                    strokeWidth={2}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )
      })}
    </div>
  )
}
